import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

export interface SubscriptionPlan {
  id: number;
  name: string;
  tier_level: number;
  price: number | null;
  icon_color: string | null;
}

interface PlanContextType {
  plans: SubscriptionPlan[];
  loading: boolean;
  getPlanByName: (name: string) => SubscriptionPlan | undefined;
  getPlanByTier: (tier: number) => SubscriptionPlan | undefined;
}

const PlanContext = createContext<PlanContextType | undefined>(undefined);

export const PlanProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const { data } = await api.get('/admin/subscription-plans');
        setPlans(data);
      } catch (err) {
        console.error("Failed to load subscription plans", err);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const getPlanByName = (name: string) => {
    if (!name) return undefined;
    return plans.find(p => p.name.toUpperCase() === name.toUpperCase());
  };

  const getPlanByTier = (tier: number) => {
    return plans.find(p => p.tier_level === tier);
  };

  return (
    <PlanContext.Provider value={{ plans, loading, getPlanByName, getPlanByTier }}>
      {children}
    </PlanContext.Provider>
  );
};

export const usePlans = () => {
  const context = useContext(PlanContext);
  if (context === undefined) {
    throw new Error('usePlans must be used within a PlanProvider');
  }
  return context;
};
